/**
 * The market benchmark tables, typed.
 *
 * These are the numbers that expire: sector multiples, owner-manager wages, labour rates. They are
 * versioned separately from the scoring config because they are per market and get recalibrated on
 * their own schedule (`benchmarks_version` in ARCHITECTURE §Versioning), and because every figure
 * the whole table is currently UNVERIFIED. Owner wages and labour rates now carry R18 evidence;
 * sector margins and analyst verification still wait on Q4 and keep G6 closed.
 *
 * Every accepted sector has a candidate wage. An unknown sector has no entry; reading one returns
 * null and the caller flags it because a borrowed number would be worse than an absent one. Gate G6
 * also forbids showing any UNVERIFIED benchmark as fact.
 */
import au from "../generated/benchmarks_au_v1.json" with { type: "json" };

export type Market = "au";

export interface MultipleRange { low: number; market: number; high: number }

export interface OwnerManagerWageMethod {
  statistic: "advertised_average";
  sample_window_months: number;
  super_rate_pct: number;
  super_source_url: string;
  workers_comp_rate_pct: number;
  workers_comp_source_url: string;
  payroll_tax_rate_pct: number;
  payroll_tax_note: string;
  rounding_aud: number;
  accessed: string;
}

export interface OwnerManagerWage {
  value: number;
  base_aud: number;
  role: string;
  n: number;
  source_url: string;
  source_updated: string;
  accessed: string;
}

export interface LabourRateMethod {
  statistic: "typical_base_salary";
  role: string;
  edition: string;
  sample_scope: string;
  annual_ordinary_hours: number;
  super_rate_pct: number;
  super_source_url: string;
  workers_comp_rate_pct: number;
  workers_comp_source_url: string;
  payroll_tax_rate_pct: number;
  payroll_tax_note: string;
  rounding_aud_per_hour: number;
  source_url: string;
  source_updated: string;
  accessed: string;
}

export interface LabourRate {
  value: number;
  base_aud: number;
  source_location: string;
  source_url: string;
  source_updated: string;
  accessed: string;
}

export interface Benchmarks {
  market: string;
  version: string;
  status: string;
  source: string;
  owner_manager_wage_method: OwnerManagerWageMethod;
  owner_manager_wage_aud: Record<string, OwnerManagerWage>;
  ebitda_multiple: Record<string, MultipleRange>;
  multiple_fallback_sector: string;
  labour_rate_method: LabourRateMethod;
  labour_rate_aud_per_hour: { regional: LabourRate; metro_by_state: Record<string, LabourRate> };
  ebitda_margin_median_pct: Record<string, MedianMargin>;
}

/**
 * A sector median margin and where it came from (DQ-71). `research` is R18, with a URL and a date
 * on the row it was taken from; `observed` is the median of the businesses we have normalised in
 * that sector ourselves, and `n` is how many. Keeping the URL in the runtime table prevents a
 * copied number from becoming detached from the evidence that justified it.
 */
export interface MedianMargin {
  value: number;
  n: number;
  source: "research" | "observed";
  source_url: string;
  accessed: string | null;
}

const TABLES: Record<Market, Benchmarks> = { au: au as unknown as Benchmarks };

export function benchmarksFor(market: Market): Benchmarks {
  const table = TABLES[market];
  if (table === undefined) throw new Error(`no benchmark table for market ${market}`);
  return table;
}

export const BENCHMARKS_VERSION_AU: string = (au as { version: string }).version;

/** Null for an unknown sector. The caller flags; it never substitutes. */
export function ownerManagerWage(b: Benchmarks, sector: string): number | null {
  return b.owner_manager_wage_aud[sector]?.value ?? null;
}

/** The sector's own range, or the `other` fallback the specification defines, saying which it gave. */
export function ebitdaMultiple(b: Benchmarks, sector: string): { range: MultipleRange; fellBack: boolean } | null {
  const own = b.ebitda_multiple[sector];
  if (own) return { range: own, fellBack: false };
  const fallback = b.ebitda_multiple[b.multiple_fallback_sector];
  return fallback ? { range: fallback, fellBack: true } : null;
}

/**
 * SCORING_SPEC §2 resolves from state and metro/regional, which is what the row carries.
 *
 * DQ-70: a state with no metropolitan rate of its own falls back to the lowest one in the table.
 * The direction is knowably safe — a lower rate understates the saving — and the rate never touches
 * the valuation. The caller is told it fell back so the run can say so.
 */
export function labourRate(
  b: Benchmarks,
  state: string,
  region: "metro" | "regional",
  fallback: "lowest_metro" | "none" = "none",
): { rate: number; fellBack: boolean } | null {
  if (region === "regional") return { rate: b.labour_rate_aud_per_hour.regional.value, fellBack: false };
  const own = b.labour_rate_aud_per_hour.metro_by_state[state];
  if (own !== undefined) return { rate: own.value, fellBack: false };
  if (fallback !== "lowest_metro") return null;
  const rates = Object.values(b.labour_rate_aud_per_hour.metro_by_state);
  if (rates.length === 0) return null;
  return { rate: Math.min(...rates.map((rate) => rate.value)), fellBack: true };
}

/**
 * The sector median EBITDA margin, and which source gave it (DQ-71).
 *
 * Our own normalised population wins once a sector has enough of it, because businesses like the
 * ones we actually see are a better comparison than an industry aggregate containing firms ten
 * times the size. Below the threshold an observed median is too thin to trust and the researched
 * figure stands. Null when neither exists — which is the state today, and is flagged, never
 * defaulted: this is the most heavily weighted input in Business Quality.
 */
export function medianMargin(
  b: Benchmarks,
  sector: string,
  observedTakesOverAtN: number,
): MedianMargin | null {
  const entry = b.ebitda_margin_median_pct[sector];
  if (entry === undefined) return null;
  if (entry.source === "observed" && entry.n < observedTakesOverAtN) return null;
  return entry;
}
